import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import {
    FiPlay,
    FiX,
    FiChevronLeft,
    FiChevronRight,
    FiPause,
    FiExternalLink,
    FiMapPin,
    FiShield,
    FiCheckCircle,
    FiClock,
    FiPercent,
    FiVideo,
    FiAlertCircle,
    FiRefreshCw
} from 'react-icons/fi';
import { FaYoutube, FaTiktok, FaWhatsapp } from 'react-icons/fa';
import {
    BUSINESS_WHATSAPP_CHAT_URL,
    BUSINESS_ADDRESS_SINGLE,
    RAHBER_LOGO_URL,
    SOCIAL_LINKS
} from '../../config/businessInfo';
import videoShortsService from '../../services/videoShortsService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import {
    formatVideoItem,
    classifyAspectRatio,
    isConstrainedNetwork,
    preconnectMediaOrigin,
    getCachedVideoDimensions,
    setCachedVideoDimensions,
    warmShortVideoMetadata,
    cancelVideoMetadataWarmup
} from '../../utils/videoUtils';
import './VideoShortsSection.css';

// In-memory cache of captured first-frame JPEG data URLs for uploaded videos without custom thumbnails
const capturedPosterCache = new Map();

// Bounded concurrency queue (max 2 simultaneous preview metadata loads) so multiple uploaded
// videos never saturate HTTP/1.1 connection limits or hardware video decoders.
const MAX_CONCURRENT_PREVIEW_LOADS = 2;
let activePreviewLoads = 0;
let previewLoadModalPaused = false;
const pendingPreviewQueue = [];

function pumpPreviewLoadQueue() {
    if (previewLoadModalPaused) return;
    while (
        activePreviewLoads < MAX_CONCURRENT_PREVIEW_LOADS &&
        pendingPreviewQueue.length > 0
    ) {
        const nextTask = pendingPreviewQueue.shift();
        if (nextTask && !nextTask.cancelled) {
            nextTask.granted = true;
            activePreviewLoads += 1;
            nextTask.onGrant();
        }
    }
}

function setPreviewQueueModalPaused(paused) {
    previewLoadModalPaused = Boolean(paused);
    if (!previewLoadModalPaused) {
        pumpPreviewLoadQueue();
    }
}

function requestPreviewLoadSlot(onGrant) {
    const task = {
        granted: false,
        cancelled: false,
        onGrant
    };

    pendingPreviewQueue.push(task);
    pumpPreviewLoadQueue();

    let released = false;
    return () => {
        if (released) return;
        released = true;
        task.cancelled = true;

        if (task.granted) {
            activePreviewLoads = Math.max(0, activePreviewLoads - 1);
            pumpPreviewLoadQueue();
        } else {
            const idx = pendingPreviewQueue.indexOf(task);
            if (idx !== -1) {
                pendingPreviewQueue.splice(idx, 1);
            }
        }
    };
}

/**
 * Attempt to snapshot a lightweight JPEG poster from a decoded <video> frame.
 * Succeeds for same-origin `/uploads/...` or CORS-enabled CDN streams so the <video>
 * element can be unmounted and its hardware decoder freed immediately.
 * Returns null silently on cross-origin streams without CORS headers.
 */
function captureVideoFrameDataUrl(videoEl) {
    if (
        !videoEl ||
        videoEl.videoWidth <= 0 ||
        videoEl.videoHeight <= 0 ||
        videoEl.readyState < 2 ||
        typeof document === 'undefined'
    ) {
        return null;
    }

    try {
        const maxDimension = 540;
        const scale = Math.min(
            1,
            maxDimension / Math.max(videoEl.videoWidth, videoEl.videoHeight)
        );
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(videoEl.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(videoEl.videoHeight * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.78);
        if (dataUrl && dataUrl.startsWith('data:image/jpeg') && dataUrl.length > 1200) {
            return dataUrl;
        }
    } catch (_) {
        // Cross-origin video without CORS headers — fall back to keeping paused <video> frame
    }
    return null;
}

/**
 * Build a stable signature string for comparing normalized video short lists
 * so background real-time sync checks do not trigger redundant re-renders.
 */
function buildVideosSignature(list) {
    if (!Array.isArray(list) || list.length === 0) return '';
    return list
        .map(
            (v) =>
                `${v._id}|${v.videoSrc || ''}|${v.rawVideoSrc || ''}|${v.thumbnail || ''}|${
                    v.youtubeId || ''
                }|${v.embedUrl || ''}|${v.title || ''}|${v.caption || ''}|${
                    v.description || ''
                }|${v.badge || ''}|${v.displayOrder}|${v.initialOrientation || ''}`
        )
        .join(';;');
}

/**
 * Resolve the best-known orientation & aspect-ratio profile for a video item.
 */
function resolveVideoProfile(short, mediaDimensionsMap) {
    if (!short) {
        return {
            ratio: 16 / 9,
            orientation: 'landscape',
            aspectClass: 'aspect-16-9',
            source: 'fallback'
        };
    }

    const detected = mediaDimensionsMap ? mediaDimensionsMap[short._id] : null;
    if (detected && detected.orientation && detected.aspectClass) {
        return detected;
    }

    const cached =
        (short.videoSrc && getCachedVideoDimensions(short.videoSrc)) ||
        (short.rawVideoSrc && getCachedVideoDimensions(short.rawVideoSrc)) ||
        null;
    if (cached && cached.orientation && cached.aspectClass) {
        return cached;
    }

    if (short.initialOrientation && short.initialOrientation !== 'unknown') {
        return {
            ratio:
                short.initialAspectRatio ||
                (short.initialOrientation === 'landscape' ? 16 / 9 : 9 / 16),
            orientation: short.initialOrientation,
            aspectClass:
                short.initialAspectClass ||
                (short.initialOrientation === 'landscape'
                    ? 'aspect-16-9'
                    : short.initialOrientation === 'square'
                      ? 'aspect-1-1'
                      : 'aspect-9-16'),
            source: 'initial'
        };
    }

    return {
        ratio: null,
        orientation: 'unknown',
        aspectClass: null,
        source: 'none'
    };
}

/**
 * Individual Short Card supporting:
 * - YouTube & custom thumbnail images with fallback
 * - Strictly viewport-gated, concurrency-throttled HTML5 <video preload="metadata"> frame capture
 *   for uploaded/direct videos without a custom thumbnail_url (automatically releasing <video> once captured)
 * - Orientation-aware rendering that avoids distorting or aggressively cropping videos
 * - Graceful loading spinners and error states
 */
const VideoShortCard = memo(function VideoShortCard({
    short,
    index,
    totalCount = 1,
    gridMode = 'portrait',
    profileOrientation = 'unknown',
    profileRatio = null,
    isMobileCard = false,
    isActiveMobileSlide = false,
    isSectionNearViewport = false,
    isModalOpen = false,
    isThumbFailed = false,
    isVideoFailed = false,
    onThumbError,
    onVideoError,
    onMediaDimensions,
    onPosterCaptured,
    onWarmup,
    onOpen
}) {
    const cardRef = useRef(null);
    const previewVideoRef = useRef(null);
    const slotReleaseRef = useRef(null);

    const posterCacheKey = short.rawVideoSrc || short.videoSrc || '';
    const [capturedPoster, setCapturedPoster] = useState(
        () => (posterCacheKey && capturedPosterCache.get(posterCacheKey)) || ''
    );
    const [useRawPreviewFallback, setUseRawPreviewFallback] = useState(false);
    const [isCardIntersecting, setIsCardIntersecting] = useState(false);
    const [hasPreviewSlot, setHasPreviewSlot] = useState(false);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isPreviewReady, setIsPreviewReady] = useState(false);

    // Sync captured poster if underlying video URL changes
    useEffect(() => {
        const cached =
            (posterCacheKey && capturedPosterCache.get(posterCacheKey)) || '';
        setCapturedPoster(cached);
        setUseRawPreviewFallback(false);
        setIsPreviewReady(Boolean(cached));
        setIsPreviewLoading(false);
        setHasPreviewSlot(false);
    }, [posterCacheKey]);

    // Observe individual card visibility so offscreen cards never download video bytes
    useEffect(() => {
        if (isCardIntersecting || !isSectionNearViewport) return undefined;
        const el = cardRef.current;
        if (!el) return undefined;

        if (typeof IntersectionObserver === 'undefined') {
            setIsCardIntersecting(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting || entry.intersectionRatio > 0) {
                        setIsCardIntersecting(true);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '220px 0px',
                threshold: 0.01
            }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [isCardIntersecting, isSectionNearViewport]);

    const hasServerThumb = Boolean(short.thumbnail) && !isThumbFailed;
    const effectiveThumb = hasServerThumb ? short.thumbnail : capturedPoster;
    const hasValidThumb = Boolean(effectiveThumb);

    const activePreviewBaseSrc =
        useRawPreviewFallback && short.rawVideoSrc
            ? short.rawVideoSrc
            : short.videoSrc || short.rawVideoSrc || '';

    const canShowVideoPreview =
        !hasValidThumb &&
        short.isDirectVideo &&
        Boolean(activePreviewBaseSrc) &&
        !isVideoFailed;

    // Only load preview metadata when section is near viewport, card is visible (or active mobile slide),
    // modal is closed, and network is not on Data Saver / 2G
    const isEligibleForPreviewLoad =
        canShowVideoPreview &&
        isSectionNearViewport &&
        !isModalOpen &&
        !isConstrainedNetwork() &&
        (isMobileCard ? isActiveMobileSlide : isCardIntersecting);

    const releaseCurrentSlot = useCallback(() => {
        if (slotReleaseRef.current) {
            const release = slotReleaseRef.current;
            slotReleaseRef.current = null;
            release();
        }
    }, []);

    // Acquire a bounded concurrency slot before mounting <video> preview
    useEffect(() => {
        if (!isEligibleForPreviewLoad || isPreviewReady) {
            if (!isPreviewReady) {
                setHasPreviewSlot(false);
                setIsPreviewLoading(false);
            }
            releaseCurrentSlot();
            return undefined;
        }

        let cancelled = false;
        const release = requestPreviewLoadSlot(() => {
            if (!cancelled) {
                setHasPreviewSlot(true);
                setIsPreviewLoading(true);
            }
        });
        slotReleaseRef.current = release;

        return () => {
            cancelled = true;
            releaseCurrentSlot();
        };
    }, [isEligibleForPreviewLoad, isPreviewReady, releaseCurrentSlot]);

    // Safety timeout so a slow stream never blocks the preview concurrency queue
    useEffect(() => {
        if (!hasPreviewSlot || isPreviewReady) return undefined;
        const timer = setTimeout(() => {
            setIsPreviewLoading(false);
            releaseCurrentSlot();
        }, 6500);
        return () => clearTimeout(timer);
    }, [hasPreviewSlot, isPreviewReady, releaseCurrentSlot]);

    const previewSrc =
        activePreviewBaseSrc && !activePreviewBaseSrc.includes('#')
            ? `${activePreviewBaseSrc}#t=0.1`
            : activePreviewBaseSrc;

    const finalizePreviewFrame = useCallback(
        (videoEl) => {
            if (!videoEl) return;
            if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
                setCachedVideoDimensions(
                    activePreviewBaseSrc,
                    videoEl.videoWidth,
                    videoEl.videoHeight
                );
                if (onMediaDimensions) {
                    onMediaDimensions(
                        short._id,
                        videoEl.videoWidth,
                        videoEl.videoHeight,
                        'video'
                    );
                }
            }

            const dataUrl = captureVideoFrameDataUrl(videoEl);
            if (dataUrl && posterCacheKey) {
                capturedPosterCache.set(posterCacheKey, dataUrl);
                setCapturedPoster(dataUrl);
                if (onPosterCaptured) {
                    onPosterCaptured(short._id, dataUrl);
                }
            }

            setIsPreviewLoading(false);
            setIsPreviewReady(true);
            releaseCurrentSlot();
        },
        [
            activePreviewBaseSrc,
            onMediaDimensions,
            onPosterCaptured,
            posterCacheKey,
            releaseCurrentSlot,
            short._id
        ]
    );

    const shouldRenderPreviewVideoElement =
        canShowVideoPreview && (isPreviewReady || (hasPreviewSlot && !isModalOpen));

    const resolvedOrientation = profileOrientation || 'unknown';
    const resolvedRatio = profileRatio;

    // Determine whether this card's container is widescreen (16:9) or portrait (9:16)
    const isContainerLandscape = isMobileCard
        ? totalCount === 1
            ? resolvedOrientation === 'landscape'
            : gridMode === 'landscape'
        : totalCount === 1 || gridMode === 'landscape';

    // Avoid cropping important video content when media orientation differs from card container
    const shouldContainMedia = isContainerLandscape
        ? resolvedOrientation === 'portrait' ||
          resolvedOrientation === 'square' ||
          (resolvedRatio !== null && (resolvedRatio < 1.38 || resolvedRatio > 2.15))
        : resolvedOrientation === 'landscape' ||
          resolvedOrientation === 'square' ||
          (resolvedRatio !== null && resolvedRatio > 0.82);

    // YouTube Shorts use 4:3 hqdefault.jpg thumbnails with baked-in letterbox/pillarbox;
    // when framed inside a widescreen desktop showcase card, crop the inner frame cleanly to 9:16.
    const useFramedVerticalThumb =
        isContainerLandscape &&
        hasServerThumb &&
        Boolean(short.youtubeId && short.isYouTubeShort);

    const promptText = short.title ? short.title : 'Tap to Watch';

    const hasMediaError =
        isVideoFailed || (!short.youtubeId && !short.videoSrc && !short.embedUrl);

    const cardClasses = [
        'shorts-card',
        isMobileCard ? 'shorts-card--mobile' : '',
        totalCount === 1 && !isMobileCard ? 'shorts-card--featured-single' : '',
        isContainerLandscape
            ? 'shorts-card--container-landscape'
            : 'shorts-card--container-portrait',
        resolvedOrientation !== 'unknown'
            ? `shorts-card--media-${resolvedOrientation}`
            : ''
    ]
        .filter(Boolean)
        .join(' ');

    const innerClasses = [
        'shorts-card-inner',
        isContainerLandscape
            ? 'shorts-card-inner--landscape'
            : 'shorts-card-inner--portrait',
        shouldContainMedia ? 'shorts-card-inner--contain' : ''
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={cardRef}
            className={cardClasses}
            onClick={() => onOpen(short)}
            onMouseEnter={() => onWarmup && onWarmup(short, false)}
            onFocus={() => onWarmup && onWarmup(short, false)}
            onTouchStart={() => onWarmup && onWarmup(short, true)}
            role="button"
            tabIndex={0}
            aria-label={`Play ${short.title || `Short ${index + 1}`}`}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(short);
                }
            }}
        >
            <div className={innerClasses}>
                {/* Subtle blurred studio backdrop when framing portrait-in-landscape or landscape-in-portrait */}
                {shouldContainMedia && hasValidThumb && (
                    <div className="shorts-card-ambient" aria-hidden="true">
                        <img
                            src={effectiveThumb}
                            alt=""
                            className="shorts-card-ambient-img"
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                        />
                    </div>
                )}

                {hasValidThumb ? (
                    useFramedVerticalThumb ? (
                        <div className="shorts-card-vertical-frame">
                            <img
                                src={effectiveThumb}
                                alt={short.title || `Rahber Corporation Short ${index + 1}`}
                                className="shorts-card-thumb shorts-card-thumb--cover"
                                loading="lazy"
                                decoding="async"
                                referrerPolicy="no-referrer"
                                onError={() => {
                                    if (hasServerThumb) {
                                        onThumbError(short._id);
                                    } else {
                                        setCapturedPoster('');
                                    }
                                }}
                            />
                        </div>
                    ) : (
                        <img
                            src={effectiveThumb}
                            alt={short.title || `Rahber Corporation Short ${index + 1}`}
                            className="shorts-card-thumb"
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer"
                            onLoad={(e) => {
                                if (hasServerThumb && !short.youtubeId && onMediaDimensions) {
                                    const img = e.currentTarget;
                                    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                                        onMediaDimensions(
                                            short._id,
                                            img.naturalWidth,
                                            img.naturalHeight,
                                            'thumb'
                                        );
                                    }
                                }
                            }}
                            onError={() => {
                                if (hasServerThumb) {
                                    onThumbError(short._id);
                                } else {
                                    setCapturedPoster('');
                                }
                            }}
                        />
                    )
                ) : canShowVideoPreview ? (
                    <>
                        {!isPreviewReady && (
                            <div
                                className="shorts-thumb-fallback shorts-thumb-fallback--preview-bg"
                                aria-hidden="true"
                            >
                                {isPreviewLoading ? (
                                    <span className="shorts-spinner" />
                                ) : (
                                    <FiVideo size={34} />
                                )}
                                <span className="shorts-thumb-fallback-title">
                                    {short.title}
                                </span>
                            </div>
                        )}
                        {shouldRenderPreviewVideoElement && (
                            <video
                                ref={previewVideoRef}
                                src={previewSrc}
                                preload="metadata"
                                muted
                                playsInline
                                webkit-playsinline="true"
                                disablePictureInPicture
                                aria-hidden="true"
                                tabIndex={-1}
                                className={`shorts-card-thumb shorts-card-video-preview ${
                                    isPreviewReady ? 'is-ready' : ''
                                }`}
                                onLoadedMetadata={(e) => {
                                    const v = e.currentTarget;
                                    if (v.videoWidth > 0 && v.videoHeight > 0) {
                                        setCachedVideoDimensions(
                                            activePreviewBaseSrc,
                                            v.videoWidth,
                                            v.videoHeight
                                        );
                                        if (onMediaDimensions) {
                                            onMediaDimensions(
                                                short._id,
                                                v.videoWidth,
                                                v.videoHeight,
                                                'video'
                                            );
                                        }
                                    }
                                }}
                                onLoadedData={(e) => finalizePreviewFrame(e.currentTarget)}
                                onSeeked={(e) => finalizePreviewFrame(e.currentTarget)}
                                onError={() => {
                                    if (
                                        !useRawPreviewFallback &&
                                        short.rawVideoSrc &&
                                        short.rawVideoSrc !== short.videoSrc
                                    ) {
                                        setUseRawPreviewFallback(true);
                                        return;
                                    }
                                    setIsPreviewLoading(false);
                                    releaseCurrentSlot();
                                    onVideoError(short._id);
                                }}
                            />
                        )}
                    </>
                ) : (
                    <div
                        className={`shorts-thumb-fallback ${
                            hasMediaError ? 'shorts-thumb-fallback--error' : ''
                        }`}
                    >
                        {hasMediaError ? <FiAlertCircle size={32} /> : <FiVideo size={36} />}
                        <span className="shorts-thumb-fallback-title">
                            {short.title}
                        </span>
                        {hasMediaError && (
                            <span className="shorts-thumb-fallback-sub">
                                Preview unavailable
                            </span>
                        )}
                    </div>
                )}

                <div className="shorts-card-overlay">
                    <div className="shorts-card-badge">
                        {short.youtubeId ? (
                            <FaYoutube className="shorts-badge-icon" aria-hidden="true" />
                        ) : (
                            <FiPlay
                                className="shorts-badge-icon shorts-badge-icon--direct"
                                aria-hidden="true"
                            />
                        )}
                        <span>{short.badge || 'Shorts'}</span>
                    </div>

                    <div className="shorts-play-button-wrap">
                        <div className="shorts-play-button" aria-hidden="true">
                            <FiPlay className="shorts-play-icon" />
                        </div>
                    </div>

                    <div className="shorts-card-footer">
                        <span
                            className="shorts-card-prompt"
                            title={short.title || 'Tap to Watch'}
                        >
                            {promptText}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
});

const VideoShortsSection = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [failedThumbnails, setFailedThumbnails] = useState({});
    const [failedVideos, setFailedVideos] = useState({});
    const [mediaDimensions, setMediaDimensions] = useState({});
    const [capturedPosters, setCapturedPosters] = useState({});
    const [activeVideo, setActiveVideo] = useState(null);
    const [useRawModalFallback, setUseRawModalFallback] = useState(false);
    const [isIframeLoading, setIsIframeLoading] = useState(false);
    const [hasActivePlaybackStarted, setHasActivePlaybackStarted] = useState(false);
    const [playerError, setPlayerError] = useState(false);
    const [playerRetryKey, setPlayerRetryKey] = useState(0);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoplay, setIsAutoplay] = useState(true);
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);
    const [isSectionNearViewport, setIsSectionNearViewport] = useState(false);
    const [isSectionVisible, setIsSectionVisible] = useState(false);
    const [isPageVisible, setIsPageVisible] = useState(() =>
        typeof document === 'undefined' ? true : document.visibilityState === 'visible'
    );
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }
        return window.matchMedia('(max-width: 640px)').matches;
    });

    const sectionRef = useRef(null);
    const activeVideoElRef = useRef(null);
    const autoplayTimerRef = useRef(null);
    const videosSignatureRef = useRef('');
    const isMountedRef = useRef(true);

    const youtubeLink =
        SOCIAL_LINKS.find((s) => s.id === 'youtube')?.href ||
        'https://www.youtube.com/@rahbercorporation';
    const tiktokLink =
        SOCIAL_LINKS.find((s) => s.id === 'tiktok')?.href ||
        'https://www.tiktok.com/@rahbercorporation';

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            setPreviewQueueModalPaused(false);
            cancelVideoMetadataWarmup();
        };
    }, []);

    // Match the exact <= 640px CSS breakpoint so only the active layout mounts <video> / <img> elements
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mql = window.matchMedia('(max-width: 640px)');
        const handleViewportChange = (e) => {
            setIsMobileViewport(e.matches);
        };

        setIsMobileViewport(mql.matches);
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', handleViewportChange);
            return () => mql.removeEventListener('change', handleViewportChange);
        }
        if (typeof mql.addListener === 'function') {
            mql.addListener(handleViewportChange);
            return () => mql.removeListener(handleViewportChange);
        }
        return undefined;
    }, []);

    // Track browser tab visibility to pause carousel timers when backgrounded
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const onVisibilityChange = () => {
            setIsPageVisible(document.visibilityState === 'visible');
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    // Record detected natural media dimensions (video metadata takes precedence over thumbnail hints)
    const handleMediaDimensions = useCallback((videoId, width, height, source = 'video') => {
        if (!videoId) return;
        const classified = classifyAspectRatio(width, height);
        if (!classified) return;

        setMediaDimensions((prev) => {
            const existing = prev[videoId];
            if (existing) {
                if (existing.source === 'video' && source !== 'video') {
                    return prev;
                }
                if (
                    existing.orientation === classified.orientation &&
                    existing.aspectClass === classified.aspectClass &&
                    existing.source === source
                ) {
                    return prev;
                }
            }
            return {
                ...prev,
                [videoId]: {
                    ...classified,
                    width,
                    height,
                    source
                }
            };
        });
    }, []);

    const handlePosterCaptured = useCallback((videoId, dataUrl) => {
        if (!videoId || !dataUrl) return;
        setCapturedPosters((prev) => {
            if (prev[videoId] === dataUrl) return prev;
            return { ...prev, [videoId]: dataUrl };
        });
    }, []);

    // Pause any other playing HTML5 <video> elements on the storefront
    const pauseAllOtherVideos = useCallback((exceptEl = null) => {
        if (typeof document === 'undefined') return;
        const videoNodes = document.querySelectorAll('video');
        videoNodes.forEach((vid) => {
            if (vid !== exceptEl && !vid.paused) {
                try {
                    vid.pause();
                } catch (_) {
                    // Ignore pause errors on detached elements
                }
            }
        });
    }, []);

    // Pre-warm origin & metadata on pointer hover / focus when modal is closed
    const handleCardWarmup = useCallback(
        (short, touchOnly = false) => {
            if (!short || activeVideo) return;
            if (short.youtubeId) {
                preconnectMediaOrigin('https://www.youtube-nocookie.com');
                preconnectMediaOrigin('https://i.ytimg.com');
                return;
            }
            if (short.isDirectVideo && short.videoSrc) {
                preconnectMediaOrigin(short.videoSrc);
                if (!touchOnly) {
                    warmShortVideoMetadata(short.videoSrc).then((dims) => {
                        if (dims && isMountedRef.current) {
                            handleMediaDimensions(
                                short._id,
                                dims.width,
                                dims.height,
                                'video'
                            );
                        }
                    });
                }
            }
        },
        [activeVideo, handleMediaDimensions]
    );

    // One-shot observer to enable lazy media loading when user scrolls near Video Shorts
    useEffect(() => {
        if (isSectionNearViewport) return undefined;
        const el = sectionRef.current;
        if (!el) return undefined;

        if (typeof IntersectionObserver === 'undefined') {
            setIsSectionNearViewport(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting || entry.intersectionRatio > 0) {
                        setIsSectionNearViewport(true);
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '280px 0px',
                threshold: 0.01
            }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [isSectionNearViewport, loading, videos.length]);

    // Continuous observer to pause mobile carousel timers whenever section leaves the viewport
    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return undefined;

        if (typeof IntersectionObserver === 'undefined') {
            setIsSectionVisible(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsSectionVisible(
                        entry.isIntersecting && entry.intersectionRatio >= 0.12
                    );
                });
            },
            {
                threshold: [0, 0.12, 0.35]
            }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [loading, videos.length]);

    // Preconnect primary media origins once section approaches viewport
    useEffect(() => {
        if (!isSectionNearViewport || videos.length === 0) return;
        videos.slice(0, 4).forEach((v) => {
            if (v.youtubeId) {
                preconnectMediaOrigin('https://www.youtube-nocookie.com');
            } else if (v.videoSrc) {
                preconnectMediaOrigin(v.videoSrc);
            }
        });
    }, [isSectionNearViewport, videos]);

    // Fetch dynamic video shorts from backend API (supports initial load & silent real-time refresh)
    const fetchVideos = useCallback(
        async (showLoadingSkeleton = false) => {
            try {
                if (showLoadingSkeleton) {
                    setLoading(true);
                }
                setError(null);
                const rawList = await videoShortsService.getAll({ active_only: true });

                if (!isMountedRef.current) return;

                if (Array.isArray(rawList) && rawList.length > 0) {
                    const normalized = rawList
                        .map((item, idx) => formatVideoItem(item, idx))
                        .filter((item) => item && item.isActive)
                        .sort((a, b) => a.displayOrder - b.displayOrder);

                    const nextSignature = buildVideosSignature(normalized);
                    if (
                        !showLoadingSkeleton &&
                        nextSignature === videosSignatureRef.current
                    ) {
                        return;
                    }
                    videosSignatureRef.current = nextSignature;

                    setVideos(normalized);

                    // Clear stale failure flags when media list changes
                    setFailedVideos({});
                    setFailedThumbnails({});

                    // Reconcile currently open modal video if updated/deleted in real-time
                    setActiveVideo((prevActive) => {
                        if (!prevActive) return null;
                        const updatedMatch = normalized.find(
                            (v) => v._id === prevActive._id || v.rawId === prevActive.rawId
                        );
                        if (!updatedMatch) {
                            pauseAllOtherVideos(null);
                            return null;
                        }
                        if (updatedMatch.videoSrc !== prevActive.videoSrc) {
                            setPlayerError(false);
                            setUseRawModalFallback(false);
                            setHasActivePlaybackStarted(false);
                            setIsIframeLoading(
                                Boolean(
                                    updatedMatch.youtubeId ||
                                        (updatedMatch.isEmbedVideo && updatedMatch.embedUrl)
                                )
                            );
                            setMediaDimensions((prevDims) => {
                                if (!prevDims[updatedMatch._id]) return prevDims;
                                const nextDims = { ...prevDims };
                                delete nextDims[updatedMatch._id];
                                return nextDims;
                            });
                        }
                        return updatedMatch;
                    });
                } else {
                    videosSignatureRef.current = '';
                    setVideos([]);
                    setActiveVideo((prevActive) => {
                        if (prevActive) pauseAllOtherVideos(null);
                        return null;
                    });
                }
            } catch (err) {
                if (!isMountedRef.current) return;
                console.warn(
                    '[VideoShortsSection] Failed to load videos from backend:',
                    err?.message || err
                );
                setError(err);
                if (showLoadingSkeleton) {
                    setVideos([]);
                }
            } finally {
                if (isMountedRef.current && showLoadingSkeleton) {
                    setLoading(false);
                }
            }
        },
        [pauseAllOtherVideos]
    );

    useEffect(() => {
        fetchVideos(true);
    }, [fetchVideos]);

    // Real-time synchronization listeners for admin uploads, replacements, reorders, toggles, or deletions
    const handleRealtimeUpdate = useCallback(() => {
        fetchVideos(false);
    }, [fetchVideos]);

    useRealtimeSync('video_shorts', handleRealtimeUpdate);
    useRealtimeSync('video-shorts', handleRealtimeUpdate);
    useRealtimeSync('videoshorts', handleRealtimeUpdate);

    // Ensure slide index stays in bounds if videos list changes
    useEffect(() => {
        if (currentSlide >= videos.length && videos.length > 0) {
            setCurrentSlide(0);
        }
    }, [videos.length, currentSlide]);

    const handleNextSlide = useCallback(() => {
        if (videos.length === 0) return;
        setCurrentSlide((prev) => (prev + 1) % videos.length);
    }, [videos.length]);

    const handlePrevSlide = useCallback(() => {
        if (videos.length === 0) return;
        setCurrentSlide((prev) => (prev - 1 + videos.length) % videos.length);
    }, [videos.length]);

    // Autoplay slideshow for mobile carousel ONLY when on mobile viewport, visible on screen, tab active, and modal closed
    useEffect(() => {
        const shouldRunCarousel =
            isMobileViewport &&
            isAutoplay &&
            isSectionVisible &&
            isPageVisible &&
            activeVideo === null &&
            videos.length > 1;

        if (!shouldRunCarousel) {
            if (autoplayTimerRef.current) {
                clearInterval(autoplayTimerRef.current);
                autoplayTimerRef.current = null;
            }
            return undefined;
        }

        autoplayTimerRef.current = setInterval(() => {
            handleNextSlide();
        }, 4200);

        return () => {
            if (autoplayTimerRef.current) {
                clearInterval(autoplayTimerRef.current);
                autoplayTimerRef.current = null;
            }
        };
    }, [
        isMobileViewport,
        isAutoplay,
        isSectionVisible,
        isPageVisible,
        activeVideo,
        videos.length,
        handleNextSlide
    ]);

    const releaseActivePlayer = useCallback(() => {
        const vid = activeVideoElRef.current;
        if (vid) {
            try {
                vid.pause();
            } catch (_) {
                // Ignore detach errors
            }
        }
    }, []);

    const closeModal = useCallback(() => {
        releaseActivePlayer();
        pauseAllOtherVideos(null);
        setPreviewQueueModalPaused(false);
        setActiveVideo(null);
        setUseRawModalFallback(false);
        setIsIframeLoading(false);
        setHasActivePlaybackStarted(false);
        setPlayerError(false);
    }, [pauseAllOtherVideos, releaseActivePlayer]);

    const openVideoModal = useCallback(
        (video) => {
            if (!video) return;
            cancelVideoMetadataWarmup();
            setPreviewQueueModalPaused(true);
            releaseActivePlayer();
            pauseAllOtherVideos(null);
            setIsAutoplay(false);

            const hasPlayableSource = Boolean(
                (video.isDirectVideo && (video.videoSrc || video.rawVideoSrc)) ||
                    video.youtubeId ||
                    (video.isEmbedVideo && video.embedUrl)
            );

            if (video.youtubeId) {
                preconnectMediaOrigin('https://www.youtube-nocookie.com');
            } else if (video.videoSrc) {
                preconnectMediaOrigin(video.videoSrc);
            }

            setUseRawModalFallback(false);
            setPlayerError(!hasPlayableSource);
            setHasActivePlaybackStarted(false);
            setIsIframeLoading(
                hasPlayableSource &&
                    Boolean(
                        video.youtubeId || (video.isEmbedVideo && video.embedUrl)
                    )
            );
            setPlayerRetryKey(0);
            setActiveVideo(video);
        },
        [pauseAllOtherVideos, releaseActivePlayer]
    );

    const handleModalStep = useCallback(
        (direction) => {
            if (!activeVideo || videos.length <= 1) return;
            const currentIdx = videos.findIndex((v) => v._id === activeVideo._id);
            if (currentIdx === -1) return;
            const nextIdx = (currentIdx + direction + videos.length) % videos.length;
            openVideoModal(videos[nextIdx]);
        },
        [activeVideo, videos, openVideoModal]
    );

    // Programmatically start native <video> playback when modal opens or switches shorts
    useEffect(() => {
        if (!activeVideo || !activeVideo.isDirectVideo || playerError) return;
        const vid = activeVideoElRef.current;
        if (!vid) return;

        pauseAllOtherVideos(vid);
        const playPromise = vid.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
                // If unmuted autoplay is restricted by browser policy, native controls remain ready
            });
        }
    }, [activeVideo, useRawModalFallback, playerRetryKey, playerError, pauseAllOtherVideos]);

    // Once active video has started playing smoothly, quietly pre-warm the next short's metadata
    // so Next/Previous navigation has zero delay or layout shift
    useEffect(() => {
        if (!activeVideo || !hasActivePlaybackStarted || videos.length <= 1) {
            return undefined;
        }

        const timer = setTimeout(() => {
            if (!isMountedRef.current) return;
            const currentIdx = videos.findIndex((v) => v._id === activeVideo._id);
            if (currentIdx === -1) return;

            const nextVideo = videos[(currentIdx + 1) % videos.length];
            if (!nextVideo) return;

            if (nextVideo.youtubeId) {
                preconnectMediaOrigin('https://www.youtube-nocookie.com');
            } else if (nextVideo.isDirectVideo && nextVideo.videoSrc) {
                warmShortVideoMetadata(nextVideo.videoSrc).then((dims) => {
                    if (dims && isMountedRef.current) {
                        handleMediaDimensions(
                            nextVideo._id,
                            dims.width,
                            dims.height,
                            'video'
                        );
                    }
                });
            }
        }, 650);

        return () => clearTimeout(timer);
    }, [activeVideo, hasActivePlaybackStarted, videos, handleMediaDimensions]);

    // Handle Escape / Arrow keys, body scroll lock, and background animation pause when modal is open
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!activeVideo) return;
            if (e.key === 'Escape') {
                closeModal();
            } else if (e.key === 'ArrowRight' && videos.length > 1) {
                handleModalStep(1);
            } else if (e.key === 'ArrowLeft' && videos.length > 1) {
                handleModalStep(-1);
            }
        };

        if (activeVideo) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('shorts-video-modal-open');
            window.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('shorts-video-modal-open');
        }

        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('shorts-video-modal-open');
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeVideo, videos.length, closeModal, handleModalStep]);

    const handleThumbnailError = useCallback((id) => {
        setFailedThumbnails((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    }, []);

    const handleVideoError = useCallback((id) => {
        setFailedVideos((prev) => (prev[id] ? prev : { ...prev, [id]: true }));
    }, []);

    const handleRetryPlayback = () => {
        if (!activeVideo) return;
        setFailedVideos((prev) => {
            if (!prev[activeVideo._id]) return prev;
            const next = { ...prev };
            delete next[activeVideo._id];
            return next;
        });
        setUseRawModalFallback(false);
        setPlayerError(false);
        setHasActivePlaybackStarted(false);
        setIsIframeLoading(
            Boolean(
                activeVideo.youtubeId ||
                    (activeVideo.isEmbedVideo && activeVideo.embedUrl)
            )
        );
        setPlayerRetryKey((k) => k + 1);
    };

    // Mobile touch swipe handling
    const handleTouchStart = (e) => {
        setTouchEndX(null);
        setTouchStartX(e.targetTouches[0].clientX);
        setIsAutoplay(false);
    };

    const handleTouchMove = (e) => {
        setTouchEndX(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (touchStartX === null || touchEndX === null) return;
        const diff = touchStartX - touchEndX;
        if (diff > 45) {
            handleNextSlide();
        } else if (diff < -45) {
            handlePrevSlide();
        }
        setTouchStartX(null);
        setTouchEndX(null);
    };

    // Memoize per-video orientation profiles so cards only re-render when their own profile changes
    const videoProfiles = useMemo(
        () => videos.map((v) => resolveVideoProfile(v, mediaDimensions)),
        [videos, mediaDimensions]
    );

    // Skeleton loader while fetching initial data
    if (loading && videos.length === 0) {
        return (
            <section
                id="video-shorts-section"
                ref={sectionRef}
                className="shorts-section"
                aria-label="Loading Rahber Corporation Video Showcase"
            >
                <div className="container shorts-container">
                    <div className="shorts-header">
                        <span className="section-eyebrow shorts-eyebrow">
                            OFFICIAL VIDEO SHORTS
                        </span>
                        <h2 className="shorts-title">
                            Watch Rahber Corporation in Action
                        </h2>
                        <p className="shorts-subtitle">
                            Explore our flagship Lahore showroom, genuine box-packed appliances, and store walkthroughs
                        </p>
                        <div className="shorts-accent-divider" aria-hidden="true" />
                    </div>

                    <div className="shorts-grid shorts-grid--skeleton" aria-hidden="true">
                        {[0, 1, 2, 3].map((i) => (
                            <div
                                key={`skeleton-${i}`}
                                className={`shorts-card shorts-card--skeleton shorts-card--skeleton-${i}`}
                            >
                                <div className="shorts-card-inner shorts-card-inner--portrait">
                                    <div className="shorts-skeleton-shimmer" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    // If API returned 0 published videos, gracefully do not render the section
    if (!loading && videos.length === 0) {
        return null;
    }

    const landscapeCount = videoProfiles.filter((p) => p.orientation === 'landscape').length;
    const portraitCount = videoProfiles.filter((p) => p.orientation === 'portrait').length;

    const gridMode =
        videos.length === 1
            ? 'landscape'
            : landscapeCount > 0 && landscapeCount >= portraitCount
              ? 'landscape'
              : 'portrait';

    const countClass =
        videos.length === 1
            ? 'shorts-grid--count-1'
            : videos.length === 2
              ? 'shorts-grid--count-2'
              : videos.length === 3
                ? 'shorts-grid--count-3'
                : videos.length === 4
                  ? 'shorts-grid--count-4'
                  : 'shorts-grid--count-many';

    // Resolve active modal player orientation, aspect class, video stream URL & poster
    const activeProfile = activeVideo ? resolveVideoProfile(activeVideo, mediaDimensions) : null;
    const activeOrientation =
        activeProfile && activeProfile.orientation !== 'unknown'
            ? activeProfile.orientation
            : 'landscape';
    const activeAspectClass =
        activeProfile && activeProfile.aspectClass
            ? activeProfile.aspectClass
            : activeOrientation === 'portrait'
              ? 'aspect-9-16'
              : activeOrientation === 'square'
                ? 'aspect-1-1'
                : 'aspect-16-9';

    const resolvedModalVideoSrc = activeVideo
        ? useRawModalFallback && activeVideo.rawVideoSrc
            ? activeVideo.rawVideoSrc
            : activeVideo.videoSrc || activeVideo.rawVideoSrc
        : null;

    const activePosterCacheKey = activeVideo
        ? activeVideo.rawVideoSrc || activeVideo.videoSrc || ''
        : '';

    const resolvedModalPoster = activeVideo
        ? (activeVideo.thumbnail && !failedThumbnails[activeVideo._id]
              ? activeVideo.thumbnail
              : capturedPosters[activeVideo._id] ||
                (activePosterCacheKey && capturedPosterCache.get(activePosterCacheKey))) ||
          undefined
        : undefined;

    const isModalOpen = Boolean(activeVideo);

    return (
        <section
            id="video-shorts-section"
            ref={sectionRef}
            className="shorts-section"
            aria-label="Rahber Corporation Video Showcase"
        >
            <div className="container shorts-container">
                {/* Centered Premium Header */}
                <div className="shorts-header">
                    <span className="section-eyebrow shorts-eyebrow">
                        OFFICIAL VIDEO SHORTS
                    </span>
                    <h2 className="shorts-title">
                        Watch Rahber Corporation in Action
                    </h2>
                    <p className="shorts-subtitle">
                        Explore our flagship Lahore showroom, genuine box-packed appliances, and store walkthroughs
                    </p>
                    <div className="shorts-accent-divider" aria-hidden="true" />
                </div>

                {!isMobileViewport ? (
                    /* Desktop, Laptop & Tablet Responsive Video Grid */
                    <div
                        className={`shorts-grid shorts-grid--desktop shorts-grid--mode-${gridMode} ${countClass}`}
                        role="region"
                        aria-label="Video shorts grid"
                    >
                        {videos.map((short, index) => {
                            const profile = videoProfiles[index];
                            return (
                                <VideoShortCard
                                    key={short._id || short.id}
                                    short={short}
                                    index={index}
                                    totalCount={videos.length}
                                    gridMode={gridMode}
                                    profileOrientation={profile?.orientation || 'unknown'}
                                    profileRatio={profile?.ratio ?? null}
                                    isMobileCard={false}
                                    isSectionNearViewport={isSectionNearViewport}
                                    isModalOpen={isModalOpen}
                                    isThumbFailed={Boolean(failedThumbnails[short._id])}
                                    isVideoFailed={Boolean(failedVideos[short._id])}
                                    onThumbError={handleThumbnailError}
                                    onVideoError={handleVideoError}
                                    onMediaDimensions={handleMediaDimensions}
                                    onPosterCaptured={handlePosterCaptured}
                                    onWarmup={handleCardWarmup}
                                    onOpen={openVideoModal}
                                />
                            );
                        })}
                    </div>
                ) : (
                    /* Mobile Touch-Friendly Swipe Carousel */
                    <div
                        className={`shorts-carousel-mobile shorts-carousel-mobile--${gridMode}`}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        role="region"
                        aria-roledescription="carousel"
                        aria-label="Video shorts mobile carousel"
                    >
                        <div
                            className="shorts-carousel-track"
                            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                        >
                            {videos.map((short, index) => {
                                const profile = videoProfiles[index];
                                return (
                                    <div
                                        key={short._id || short.id}
                                        className="shorts-carousel-slide"
                                        aria-hidden={currentSlide !== index}
                                    >
                                        <VideoShortCard
                                            short={short}
                                            index={index}
                                            totalCount={videos.length}
                                            gridMode={gridMode}
                                            profileOrientation={
                                                profile?.orientation || 'unknown'
                                            }
                                            profileRatio={profile?.ratio ?? null}
                                            isMobileCard
                                            isActiveMobileSlide={currentSlide === index}
                                            isSectionNearViewport={isSectionNearViewport}
                                            isModalOpen={isModalOpen}
                                            isThumbFailed={Boolean(failedThumbnails[short._id])}
                                            isVideoFailed={Boolean(failedVideos[short._id])}
                                            onThumbError={handleThumbnailError}
                                            onVideoError={handleVideoError}
                                            onMediaDimensions={handleMediaDimensions}
                                            onPosterCaptured={handlePosterCaptured}
                                            onWarmup={handleCardWarmup}
                                            onOpen={openVideoModal}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        {/* Mobile Navigation Controls & Pause/Resume */}
                        {videos.length > 1 && (
                            <div className="shorts-carousel-controls">
                                <button
                                    type="button"
                                    className="shorts-nav-btn shorts-nav-btn--prev"
                                    onClick={() => {
                                        setIsAutoplay(false);
                                        handlePrevSlide();
                                    }}
                                    aria-label="Previous video"
                                    title="Previous video"
                                >
                                    <FiChevronLeft />
                                </button>

                                <div className="shorts-indicators" aria-label="Slide indicators">
                                    {videos.map((_, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className={`shorts-indicator-dot ${
                                                currentSlide === idx ? 'is-active' : ''
                                            }`}
                                            onClick={() => {
                                                setIsAutoplay(false);
                                                setCurrentSlide(idx);
                                            }}
                                            aria-label={`Go to video slide ${idx + 1}`}
                                        />
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    className="shorts-nav-btn shorts-nav-btn--next"
                                    onClick={() => {
                                        setIsAutoplay(false);
                                        handleNextSlide();
                                    }}
                                    aria-label="Next video"
                                    title="Next video"
                                >
                                    <FiChevronRight />
                                </button>

                                <button
                                    type="button"
                                    className="shorts-pause-btn"
                                    onClick={() => setIsAutoplay((prev) => !prev)}
                                    aria-label={
                                        isAutoplay
                                            ? 'Pause automatic slideshow'
                                            : 'Resume automatic slideshow'
                                    }
                                    title={isAutoplay ? 'Pause slideshow' : 'Resume slideshow'}
                                >
                                    {isAutoplay ? (
                                        <>
                                            <FiPause className="shorts-pause-icon" />
                                            <span>Pause</span>
                                        </>
                                    ) : (
                                        <>
                                            <FiPlay className="shorts-pause-icon" />
                                            <span>Play</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Social Channels Actions with Premium Glow */}
                <div className="shorts-social-actions">
                    <a
                        href={youtubeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shorts-social-btn shorts-social-btn--yt shorts-glow-btn"
                        title="Watch full videos on Rahber Corporation YouTube Channel"
                    >
                        <FaYoutube className="shorts-social-icon" aria-hidden="true" />
                        <span>Watch on YouTube</span>
                        <FiExternalLink className="shorts-external-icon" aria-hidden="true" />
                    </a>

                    {tiktokLink && (
                        <a
                            href={tiktokLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shorts-social-btn shorts-social-btn--tt shorts-glow-btn"
                            title="Follow Rahber Corporation on TikTok"
                        >
                            <FaTiktok className="shorts-social-icon" aria-hidden="true" />
                            <span>Follow on TikTok</span>
                            <FiExternalLink className="shorts-external-icon" aria-hidden="true" />
                        </a>
                    )}
                </div>
            </div>

            {/* RESPONSIVE VIDEO SHOWCASE MODAL (ADAPTS TO DESKTOP, LAPTOP, TABLET & MOBILE) */}
            {activeVideo && (
                <div
                    className="shorts-modal-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            closeModal();
                        }
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-label={activeVideo.title || 'Video Player'}
                >
                    <div
                        className={`shorts-riseup-modal shorts-riseup-modal--${activeOrientation}`}
                    >
                        {/* Top Bar */}
                        <div className="shorts-riseup-topbar">
                            <div className="shorts-riseup-brand-info">
                                <div className="shorts-riseup-avatar">
                                    <img src={RAHBER_LOGO_URL} alt="Rahber Corporation" />
                                </div>
                                <div className="shorts-riseup-header-text">
                                    <h4 className="shorts-riseup-header-title">
                                        {activeVideo.title ||
                                            'Rahber Corporation | Official Showroom Walkthrough'}
                                    </h4>
                                    <span className="shorts-riseup-header-sub">
                                        {activeVideo.caption ||
                                            'Flagship Electronics Store • Lahore'}
                                    </span>
                                </div>
                            </div>

                            <div className="shorts-riseup-topbar-actions">
                                <button
                                    type="button"
                                    className="shorts-riseup-close-round"
                                    onClick={closeModal}
                                    aria-label="Close modal"
                                    title="Close (Escape)"
                                >
                                    <FiX />
                                </button>
                            </div>
                        </div>

                        {/* Adaptive Video Player Stage */}
                        <div
                            className={`shorts-riseup-stage shorts-riseup-stage--${activeOrientation} ${
                                videos.length > 1
                                    ? 'shorts-riseup-stage--has-nav'
                                    : 'shorts-riseup-stage--single'
                            }`}
                        >
                            {/* Professional ambient backdrop for portrait/square framing on larger screens */}
                            {resolvedModalPoster && (
                                <div className="shorts-stage-ambient" aria-hidden="true">
                                    <img
                                        src={resolvedModalPoster}
                                        alt=""
                                        decoding="async"
                                        className="shorts-stage-ambient-img"
                                    />
                                </div>
                            )}

                            {videos.length > 1 && (
                                <button
                                    type="button"
                                    className="shorts-stage-nav-btn shorts-stage-nav-btn--prev"
                                    onClick={() => handleModalStep(-1)}
                                    aria-label="Previous video short"
                                    title="Previous short"
                                >
                                    <FiChevronLeft />
                                </button>
                            )}

                            <div
                                className={`shorts-riseup-player-wrapper shorts-riseup-player-wrapper--${activeOrientation} ${activeAspectClass}`}
                            >
                                {playerError ? (
                                    <div className="shorts-player-error-state" role="alert">
                                        <FiAlertCircle className="shorts-player-error-icon" />
                                        <p className="shorts-player-error-title">
                                            Video unavailable
                                        </p>
                                        <p className="shorts-player-error-desc">
                                            This video file could not be loaded or is in an unsupported format.
                                        </p>
                                        <button
                                            type="button"
                                            className="shorts-player-retry-btn"
                                            onClick={handleRetryPlayback}
                                        >
                                            <FiRefreshCw />
                                            <span>Try Again</span>
                                        </button>
                                    </div>
                                ) : activeVideo.isDirectVideo && resolvedModalVideoSrc ? (
                                    <video
                                        ref={activeVideoElRef}
                                        key={`${activeVideo._id || activeVideo.id}-${
                                            useRawModalFallback ? 'raw' : 'opt'
                                        }-${playerRetryKey}`}
                                        src={resolvedModalVideoSrc}
                                        poster={resolvedModalPoster}
                                        controls
                                        controlsList="nodownload"
                                        autoPlay
                                        playsInline
                                        webkit-playsinline="true"
                                        preload="auto"
                                        className="shorts-riseup-video-native"
                                        onLoadedMetadata={(e) => {
                                            const v = e.currentTarget;
                                            if (v.videoWidth > 0 && v.videoHeight > 0) {
                                                setCachedVideoDimensions(
                                                    resolvedModalVideoSrc,
                                                    v.videoWidth,
                                                    v.videoHeight
                                                );
                                                handleMediaDimensions(
                                                    activeVideo._id,
                                                    v.videoWidth,
                                                    v.videoHeight,
                                                    'video'
                                                );
                                            }
                                        }}
                                        onPlaying={(e) => {
                                            pauseAllOtherVideos(e.currentTarget);
                                            setHasActivePlaybackStarted(true);
                                        }}
                                        onPlay={(e) => {
                                            pauseAllOtherVideos(e.currentTarget);
                                        }}
                                        onError={() => {
                                            if (
                                                !useRawModalFallback &&
                                                activeVideo.rawVideoSrc &&
                                                activeVideo.rawVideoSrc !== activeVideo.videoSrc
                                            ) {
                                                setUseRawModalFallback(true);
                                                return;
                                            }
                                            setPlayerError(true);
                                            handleVideoError(activeVideo._id);
                                        }}
                                    />
                                ) : activeVideo.youtubeId ? (
                                    <>
                                        {isIframeLoading && (
                                            <div
                                                className="shorts-player-loader"
                                                aria-live="polite"
                                                aria-label="Loading YouTube Short"
                                            >
                                                <span className="shorts-spinner shorts-spinner--lg" />
                                            </div>
                                        )}
                                        <iframe
                                            key={`${activeVideo.youtubeId}-${playerRetryKey}`}
                                            src={
                                                activeVideo.youtubeEmbedUrl ||
                                                `https://www.youtube-nocookie.com/embed/${activeVideo.youtubeId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`
                                            }
                                            title={
                                                activeVideo.title ||
                                                'Rahber Corporation YouTube Short'
                                            }
                                            className="shorts-riseup-iframe"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen
                                            onLoad={() => {
                                                setIsIframeLoading(false);
                                                setHasActivePlaybackStarted(true);
                                            }}
                                            onError={() => {
                                                setIsIframeLoading(false);
                                                setPlayerError(true);
                                            }}
                                        />
                                    </>
                                ) : activeVideo.isEmbedVideo && activeVideo.embedUrl ? (
                                    <>
                                        {isIframeLoading && (
                                            <div
                                                className="shorts-player-loader"
                                                aria-live="polite"
                                                aria-label="Loading embedded video"
                                            >
                                                <span className="shorts-spinner shorts-spinner--lg" />
                                            </div>
                                        )}
                                        <iframe
                                            key={`${activeVideo._id || activeVideo.id}-${playerRetryKey}`}
                                            src={activeVideo.embedUrl}
                                            title={activeVideo.title || 'Video Player'}
                                            className="shorts-riseup-iframe"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                            allowFullScreen
                                            onLoad={() => {
                                                setIsIframeLoading(false);
                                                setHasActivePlaybackStarted(true);
                                            }}
                                            onError={() => {
                                                setIsIframeLoading(false);
                                                setPlayerError(true);
                                            }}
                                        />
                                    </>
                                ) : null}
                            </div>

                            {videos.length > 1 && (
                                <button
                                    type="button"
                                    className="shorts-stage-nav-btn shorts-stage-nav-btn--next"
                                    onClick={() => handleModalStep(1)}
                                    aria-label="Next video short"
                                    title="Next short"
                                >
                                    <FiChevronRight />
                                </button>
                            )}
                        </div>

                        {/* Rich Information Panel */}
                        <div className="shorts-riseup-info-panel">
                            {/* Tags Row */}
                            <div className="shorts-riseup-meta-row">
                                <div className="shorts-riseup-tags-left">
                                    <span className="shorts-riseup-pill shorts-riseup-pill--orange">
                                        {activeVideo.badge || 'OFFICIAL SHOWROOM WALKTHROUGH'}
                                    </span>
                                    <span className="shorts-riseup-pill shorts-riseup-pill--muted">
                                        100% Genuine Box-Packed
                                    </span>
                                </div>
                                <div className="shorts-riseup-tags-right">
                                    <span className="shorts-riseup-verified">
                                        <FiCheckCircle className="verified-icon" /> Sanda Kalan, Lahore • Official Warranty
                                    </span>
                                </div>
                            </div>

                            {/* Main Title & Caption */}
                            <h3 className="shorts-riseup-title">
                                {activeVideo.title || 'Official Showroom Walkthrough & Live Demos'}
                            </h3>
                            <p className="shorts-riseup-subtitle">
                                {activeVideo.caption ||
                                    'Main Bazar Chandni Chowk, Sanda Kalan, Lahore • Authorized Partner'}
                            </p>

                            <p className="shorts-riseup-description">
                                {activeVideo.description ||
                                    '100% genuine inverter ACs, freezers & 4K TVs. Available on instant cash discount or easy 4 to 12 month installments (4 month 0% markup).'}
                            </p>

                            {/* 4 Feature Specification Cards */}
                            <div className="shorts-riseup-specs-grid">
                                <div className="shorts-riseup-spec-card">
                                    <div className="spec-icon-label">
                                        <FiMapPin className="spec-icon" />
                                        <span>SHOWROOM LOCATION</span>
                                    </div>
                                    <strong className="spec-value">{BUSINESS_ADDRESS_SINGLE}</strong>
                                </div>

                                <div className="shorts-riseup-spec-card">
                                    <div className="spec-icon-label">
                                        <FiPercent className="spec-icon" />
                                        <span>CASH SAVINGS</span>
                                    </div>
                                    <strong className="spec-value">Instant Cash Discount</strong>
                                </div>

                                <div className="shorts-riseup-spec-card">
                                    <div className="spec-icon-label">
                                        <FiClock className="spec-icon" />
                                        <span>INSTALLMENT PLANS</span>
                                    </div>
                                    <strong className="spec-value">3 to 12 Month Tenures</strong>
                                </div>

                                <div className="shorts-riseup-spec-card">
                                    <div className="spec-icon-label">
                                        <FiShield className="spec-icon" />
                                        <span>BRAND WARRANTY</span>
                                    </div>
                                    <strong className="spec-value">Official Manufacturer Cards</strong>
                                </div>
                            </div>

                            {/* Balanced Action Buttons */}
                            <div className="shorts-riseup-actions-row">
                                <Link
                                    to="/contact"
                                    onClick={closeModal}
                                    className="shorts-riseup-btn shorts-riseup-btn--primary"
                                    title="Visit Flagship Showroom in Sanda Kalan Lahore"
                                >
                                    <FiMapPin className="shorts-btn-icon" />
                                    <span>Visit Showroom</span>
                                </Link>

                                <a
                                    href={BUSINESS_WHATSAPP_CHAT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shorts-riseup-btn shorts-riseup-btn--whatsapp"
                                    title="Inquire on WhatsApp with Showroom Specialists"
                                >
                                    <FaWhatsapp className="shorts-btn-icon" />
                                    <span>WhatsApp Chat</span>
                                </a>

                                <a
                                    href={activeVideo.watchUrl || youtubeLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shorts-riseup-btn shorts-riseup-btn--youtube"
                                    title={
                                        activeVideo.youtubeId
                                            ? 'Watch Full Video on YouTube'
                                            : 'Open Video in New Tab'
                                    }
                                >
                                    {activeVideo.youtubeId ? (
                                        <FaYoutube className="shorts-btn-icon" />
                                    ) : (
                                        <FiPlay className="shorts-btn-icon" />
                                    )}
                                    <span>Watch Video</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default VideoShortsSection;

